import React, { useEffect, useState } from 'react'
import { navigate } from '@reach/router'
import { makeStyles } from '@material-ui/core/styles'
import { Grid, IconButton, Tooltip, withStyles } from '@material-ui/core'
import { Heading, SubHeading } from '../../components/Text'
import { LanguageAwareComponent, Step } from '../../components'
import { useTranslation } from 'react-i18next'
import { normalizeLanguage } from '../../util'
import { useStoreActions, useStoreState } from '../../store/store.hooks'
import {
  CONSULTANCY_TYPES,
  MYMEDIO_URLS,
  FEATURES,
  SPECIALIZATIONS,
} from '../../constants'
import { isIosSafari, isLocal, isStaging } from '../../util/browser'
import { TechnicalDifficultiesPopup } from '@/components'
import { Typography } from '@material-ui/core'
import { HelpOutline as HelpOutlineIcon } from '@material-ui/icons'

const useStyles = makeStyles((theme) => ({
  blogContainer: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  dashBoardContainer: {
    padding: '60px 30px',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    backgroundColor: '#fff',
    borderRadius: '15px',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: '20px',
    },
  },

  extraDescription: {
    margin: '4px 0',
  },

  helpOutlineContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },

  helpOutlineIcon: {
    color: 'white',
  },

  step: {
    height: '100%',
  },
  tileContainer: {
    marginTop: theme.spacing(1),
  },

  tooltip: {
    backgroundColor: 'yellow',
  },

  gridRow: {
    marginTop: theme.spacing(1),
  },
}))

const CustomTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: '#222', // background of the balloon
    color: '#fff', // text color
    fontSize: '14px', // font size
    padding: '12px 16px', // padding inside balloon
    borderRadius: '8px', // optional: rounded corners
    boxShadow: '0px 2px 8px rgba(0,0,0,0.3)', // optional: shadow
  },
}))(Tooltip)

// Keeps the tile order, except that a full width tile never leaves a half
// width tile alone in its row: it waits until the next half width tile has
// filled the row.
const arrangeTiles = (tiles) => {
  const arranged = []
  let waitingFullWidthTiles = []
  let isRowHalfFilled = false

  for (const tile of tiles) {
    if (tile.fullWidth && isRowHalfFilled) {
      waitingFullWidthTiles.push(tile)
    } else {
      arranged.push(tile)

      if (!tile.fullWidth) {
        isRowHalfFilled = !isRowHalfFilled

        if (!isRowHalfFilled) {
          arranged.push(...waitingFullWidthTiles)
          waitingFullWidthTiles = []
        }
      }
    }
  }

  return [...arranged, ...waitingFullWidthTiles]
}

export const DefaultServices = () => {
  const classes = useStyles()
  const {
    fetchRequesterInfo,
    setProviderTypeId,
    setSpecialization,
    setProductSku,
    resetRequestState,
  } = useStoreActions((actions) => actions.request)
  const { fetchSubscription } = useStoreActions(
    (actions) => actions.subscription
  )
  const { fetchSettings } = useStoreActions((actions) => actions.settings)
  const { fetchIntegration } = useStoreActions((actions) => actions.integration)

  const { settingsByKey } = useStoreState((state) => state.settings)
  const integration = useStoreState((state) => state.integration)
  const isGeneralPublic = useStoreState(
    (state) => state.subscription.isGeneralPublic
  )
  const subscription = useStoreState((state) => state.subscription)
  const [showTechnicalDifficultiesPopup, setShowTechnicalDifficultiesPopup] =
    useState(false)

  const { i18n, t } = useTranslation('services')
  const language = normalizeLanguage(i18n.language)
  const isInternational = language !== 'hu'

  const shouldNavigateToOutageView = isIosSafari() //&& !isLocal() //&& !isStaging()

  useEffect(() => {
    // Fetch the subscription information so that we'll have pricing information later
    fetchSubscription()
  }, [fetchSubscription])

  useEffect(() => {
    // Reset the selected product
    setProductSku(undefined)
  }, [setProductSku])

  // Fetch the settings from the server
  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  useEffect(() => {
    fetchIntegration()
  }, [fetchIntegration])

  useEffect(() => {
    // Clear the specialization so that if the user returns here, she can
    // have a fresh start
    setSpecialization(SPECIALIZATIONS.NONE)
    resetRequestState()
  }, [])

  useEffect(() => {
    if (subscription) {
      const { account } = subscription
      const hasBeenShown =
        sessionStorage.getItem('shownTechnicalDifficultiesPopup') === 'true'

      if (!hasBeenShown) {
        setShowTechnicalDifficultiesPopup(
          account.showTechnicalDifficultiesPopup
        )
      }
    }
  }, [subscription])

  const navigateToInstantConsultation = () => {
    if (shouldNavigateToOutageView) {
      navigate('/sorry')
    } else {
      setProviderTypeId(CONSULTANCY_TYPES.DOCTOR)
      setSpecialization(undefined)
      setProductSku(
        language === 'hu'
          ? FEATURES.ON_DEMAND_MEDICAL_DOCTOR
          : FEATURES.ON_DEMAND_MEDICAL_DOCTOR_INTL
      )

      if (isGeneralPublic) {
        navigate('/patient/dashboard')
      } else {
        navigate('/patient/complaints')
      }
    }
  }

  const navigateToPediatrics = () => {
    if (shouldNavigateToOutageView) {
      navigate('/sorry')
    } else {
      // Set the provider type ID, then the specialization and skip the dashboard
      setProviderTypeId(CONSULTANCY_TYPES.DOCTOR)
      setSpecialization(SPECIALIZATIONS.PEDIATRICS)
      setProductSku(
        language === 'hu'
          ? FEATURES.ON_DEMAND_PEDIATRICIAN
          : FEATURES.ON_DEMAND_PEDIATRICIAN_INTL
      )
      // This needs to fire here, because we may be skipping the Dashboard and
      // navigating to the complaint list directly, if a specialization
      // is selected
      fetchRequesterInfo()
      navigate('/patient/complaints')
    }
  }

  const navigateToRiskAnalysis = () => {
    navigate('/patient/surveys/risk-analysis')
  }

  const navigateToMentalWellbeingDiagnostics = () => {
    navigate('/patient/surveys/who-10')
  }

  const navigateToKnowledgeBase = () => {
    navigate('/patient/knowledge-base')
  }

  const navigateInNewTab = (url) => {
    if (!url) return

    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  const navigateToBlog = () => {
    navigateInNewTab('https://blog.dokiapp.hu/')
  }

  const navigateToCorporateDiagnostics = () => {
    navigate('/patient/surveys/corporate-diagnostics')
  }

  const navigateToMyMedioUrl = (mymedioUrl) => {
    const url = settingsByKey[mymedioUrl]

    if (!url) {
      console.error(`Tried to navigate to an unknown MyMedio URL`, mymedioUrl)
    }

    navigateInNewTab(url)
  }

  const navigateToAppointmentBooking = () => {
    if (integration && integration.myMedioSiteUrl) {
      navigate(integration.myMedioSiteUrl)
    }
  }

  const navigateToGroupTherapy = () => {
    navigate('/group-therapy-signup')
  }

  const navigateToWebinars = () => {
    navigate('/webinars')
  }

  const navigateToCrisisIntervention = () => {
    navigate('/patient/services/crisis-intervention')
  }

  const navigateToMentalAcademy = () => {
    // if (isInternational) {
    //   navigateToMyMedioUrl(MYMEDIO_URLS.MENTAL_ACADEMY_INTL)
    // } else {
    //   navigateToMyMedioUrl(MYMEDIO_URLS.MENTAL_ACADEMY)
    // }
    navigate('/patient/mental-academy')
  }

  const hasFeature = (sku) =>
    Boolean(
      subscription.accountFeaturesBySku &&
        subscription.accountFeaturesBySku[sku]
    )

  const tiles = [
    {
      key: 'md',
      visible: hasFeature(
        isInternational
          ? FEATURES.ON_DEMAND_MEDICAL_DOCTOR_INTL
          : FEATURES.ON_DEMAND_MEDICAL_DOCTOR
      ),
      content: (
        <Step
          theme="md"
          title={t('instantConsultationTitle')}
          description={t('instantConsultationDescription')}
          extraDescription={
            <>
              <Typography className={classes.extraDescription}>
                {t('instantConsultationDescription2')}
              </Typography>
              <Typography className={classes.extraDescription}>
                {t('instantConsultationDescription3')}
              </Typography>
              <Typography className={classes.helpOutlineContainer}>
                {t('instantConsultationDescription4')}
                <CustomTooltip title={t('instantConsultationInfoBubble')}>
                  <IconButton size="small">
                    <HelpOutlineIcon className={classes.helpOutlineIcon} />
                  </IconButton>
                </CustomTooltip>
              </Typography>
            </>
          }
          onNavigate={navigateToInstantConsultation}
        />
      ),
    },
    {
      key: 'pediatrics',
      visible: hasFeature(
        isInternational
          ? FEATURES.ON_DEMAND_PEDIATRICIAN_INTL
          : FEATURES.ON_DEMAND_PEDIATRICIAN
      ),
      content: (
        <Step
          theme="pediatrics"
          title={t('pediatricsHeader')}
          description={t('pediatricsDetails')}
          onNavigate={navigateToPediatrics}
        />
      ),
    },
    {
      key: 'crisisIntervention',
      // Hidden while the form URL of the language is not configured
      visible:
        language !== 'ua' &&
        hasFeature(FEATURES.CRISIS_INTERVENTION) &&
        Boolean(
          settingsByKey &&
            settingsByKey[
              isInternational
                ? MYMEDIO_URLS.CRISIS_INTERVENTION_FORM_INTL
                : MYMEDIO_URLS.CRISIS_INTERVENTION_FORM
            ]
        ),
      content: (
        <Step
          theme="crisisIntervention"
          title={t('crisisInterventionTitle')}
          description={t('crisisInterventionDetails')}
          onNavigate={navigateToCrisisIntervention}
          buttonText={t('crisisInterventionButtonText')}
        />
      ),
    },
    {
      key: 'appointmentBooking',
      fullWidth: true,
      visible:
        !['ua', 'en'].includes(language) &&
        hasFeature(FEATURES.MYMEDIO_APPOINTMENT_BOOKING),
      content: (
        <Step
          theme="appointmentBooking"
          title={t('appointmentBookingTitle')}
          description={t('appointmentBookingDetails')}
          onNavigate={navigateToAppointmentBooking}
          buttonText={t('appointmentBookingButtonText')}
        />
      ),
    },
    {
      key: 'therapist',
      visible: hasFeature(
        isInternational
          ? FEATURES.THERAPIST_APPOINTMENT_INTL
          : FEATURES.THERAPIST_APPOINTMENT
      ),
      content: (
        <Step
          theme="therapist"
          title={t('schedulerTitle')}
          onNavigate={() =>
            navigateToMyMedioUrl(
              isInternational
                ? MYMEDIO_URLS.THERAPIST_INTL
                : MYMEDIO_URLS.THERAPIST
            )
          }
          buttonText={t('schedulerButtonText')}
        />
      ),
    },
    {
      key: 'dietitian',
      visible: hasFeature(
        isInternational
          ? FEATURES.DIETITIAN_APPOINTMENT_INTL
          : FEATURES.DIETITIAN_APPOINTMENT
      ),
      content: (
        <Step
          theme="dietitian"
          title={t('dietitianTitle')}
          onNavigate={() =>
            navigateToMyMedioUrl(
              isInternational
                ? MYMEDIO_URLS.DIETITIAN_INTL
                : MYMEDIO_URLS.DIETITIAN
            )
          }
          buttonText={t('dietitianButtonText')}
        />
      ),
    },
    {
      key: 'therapeuticExercise',
      visible:
        language !== 'ua' &&
        hasFeature(
          isInternational
            ? FEATURES.PHYSICAL_THERAPY_APPOINTMENT_INTL
            : FEATURES.PHYSICAL_THERAPY_APPOINTMENT
        ),
      content: (
        <Step
          theme="therapeuticExercise"
          title={t('therapeuticExerciseTitle')}
          buttonText={t('therapeuticExerciseButtonText')}
          onNavigate={() =>
            navigateToMyMedioUrl(
              isInternational
                ? MYMEDIO_URLS.PHYSICAL_THERAPY_INTL
                : MYMEDIO_URLS.PHYSICAL_THERAPY
            )
          }
        />
      ),
    },
    {
      key: 'pharmacist',
      visible: hasFeature(
        isInternational
          ? FEATURES.PHARMACIST_APPOINTMENT_INTL
          : FEATURES.PHARMACIST_APPOINTMENT
      ),
      content: (
        <Step
          theme="pharmacist"
          title={t('pharmacistTitle')}
          onNavigate={() =>
            navigateToMyMedioUrl(
              isInternational
                ? MYMEDIO_URLS.PHARMACIST_INTL
                : MYMEDIO_URLS.PHARMACIST
            )
          }
          buttonText={t('pharmacistButtonText')}
        />
      ),
    },
    {
      key: 'lactationConsultant',
      visible:
        language !== 'ua' &&
        hasFeature(
          isInternational
            ? FEATURES.LACTATION_CONSULTATION_APPOINTMENT_INTL
            : FEATURES.LACTATION_CONSULTATION_APPOINTMENT
        ),
      content: (
        <Step
          theme="lactationConsultant"
          title={t('lactationConsultantTitle')}
          onNavigate={() =>
            navigateToMyMedioUrl(
              isInternational
                ? MYMEDIO_URLS.LACTATION_CONSULTANT_INTL
                : MYMEDIO_URLS.LACTATION_CONSULTANT
            )
          }
          buttonText={t('lactationConsultantButtonText')}
        />
      ),
    },
    {
      key: 'mentalAcademy',
      fullWidth: true,
      visible: hasFeature(FEATURES.MENTAL_ACADEMY),
      content: (
        <Step
          theme="mentalAcademy"
          title={t('mentalAcademyTitle')}
          description={t('mentalAcademyDetails')}
          onNavigate={navigateToMentalAcademy}
          buttonText={t('mentalAcademyButtonText')}
        />
      ),
    },
    {
      key: 'childTherapy',
      visible:
        language !== 'ua' &&
        hasFeature(
          isInternational
            ? FEATURES.CHILD_THERAPIST_APPOINTMENT_INTL
            : FEATURES.CHILD_THERAPIST_APPOINTMENT
        ),
      content: (
        <Step
          theme="childTherapy"
          title={t('childTherapyTitle')}
          onNavigate={() =>
            navigateToMyMedioUrl(
              isInternational
                ? MYMEDIO_URLS.CHILD_THERAPIST_INTL
                : MYMEDIO_URLS.CHILD_THERAPIST
            )
          }
          buttonText={t('childTherapyButtonText')}
        />
      ),
    },
    {
      key: 'riskAnalysis',
      visible: hasFeature(FEATURES.RISK_ANALYSIS),
      content: (
        <Step
          theme="riskAnalysis"
          title={t('riskAnalysisTitle')}
          onNavigate={navigateToRiskAnalysis}
        />
      ),
    },
    {
      key: 'corporateDiagnostics',
      visible:
        !['en', 'ua'].includes(language) &&
        hasFeature(FEATURES.CORPORATE_DIAGNOSTICS),
      content: (
        <Step
          theme="corporateDiagnostics"
          title={t('corporateDiagnosticsHeader')}
          description={t('corporateDiagnosticsDetails')}
          onNavigate={navigateToCorporateDiagnostics}
        />
      ),
    },
    {
      key: 'mentalWellbeingDiagnostics',
      fullWidth: true,
      visible: hasFeature(FEATURES.MENTAL_WELLBEING_DIAGNOSTICS),
      content: (
        <Step
          theme="mentalWellbeingDiagnostics"
          title={t('mentalWellbeingDiagnosticsTitle')}
          description={t('mentalWellbeingDiagnosticsDetails')}
          onNavigate={navigateToMentalWellbeingDiagnostics}
          buttonText={t('mentalWellbeingDiagnosticsButtonText')}
        />
      ),
    },
    {
      key: 'kb',
      visible: hasFeature(FEATURES.KNOWLEDGE_BASE),
      content: (
        <Step
          theme="kb"
          title={t('kbTitle')}
          onNavigate={navigateToKnowledgeBase}
        />
      ),
    },
    {
      key: 'groupTherapy',
      visible:
        !['en', 'ua'].includes(language) && hasFeature(FEATURES.GROUP_THERAPY),
      content: (
        <Step
          theme="groupTherapy"
          title={t('groupTherapyTitle')}
          description={t('groupTherapyDetails')}
          onNavigate={navigateToGroupTherapy}
          buttonText={t('groupTherapyButtonText')}
        />
      ),
    },
    {
      key: 'webinars',
      visible:
        !['en', 'ua'].includes(language) &&
        hasFeature(
          isInternational ? FEATURES.WEBINARS_INTL : FEATURES.WEBINARS
        ),
      content: (
        <Step
          theme="webinars"
          title={t('webinarsTitle')}
          description={t('webinarsDetails')}
          onNavigate={navigateToWebinars}
          buttonText={t('webinarsButtonText')}
        />
      ),
    },
  ]

  return (
    <>
      <Grid item xs={12} className={classes.dashBoardContainer}>
        <Heading>{t('heading')}</Heading>
        <SubHeading>{t('subHeading')}</SubHeading>
        <Grid container spacing={2} className={classes.gridRow}>
          {arrangeTiles(tiles.filter((tile) => tile.visible)).map((tile) => (
            <Grid key={tile.key} item md={tile.fullWidth ? 12 : 6} xs={12}>
              {tile.content}
            </Grid>
          ))}
        </Grid>

        <LanguageAwareComponent expectedLanguage={['en', 'ua']} action="hide">
          <Grid container spacing={2} className={classes.gridRow}>
            <Grid item xs={12} sm={12}>
              <Step
                fullWidth
                theme="blog"
                title={t('blogHeader')}
                description={t('blogDetails')}
                onNavigate={navigateToBlog}
              />
            </Grid>
          </Grid>
        </LanguageAwareComponent>
      </Grid>

      {showTechnicalDifficultiesPopup && (
        <TechnicalDifficultiesPopup open={showTechnicalDifficultiesPopup} />
      )}
    </>
  )
}
